import { DropdownOption, GameKey, PlayerFiltersPosition, TabItem } from '@dreamteam-monorepo/ui';

export const teamPageTabs: TabItem[] = [
  {
    id: '1',
    label: 'Current Gameweek',
  },
  {
    id: '2',
    label: 'Manage Team',
  },
];

export const getTeamPageTabs = (
  currentMatchdayName?: string | null,
  nextMatchdayName?: string | null,
  isLastGameweek?: boolean
): TabItem[] => {
  const tabs: TabItem[] = [
    {
      id: '1',
      label: currentMatchdayName?.trim() || teamPageTabs[0]?.label || 'Current Matchday',
    },
  ];

  if (!isLastGameweek) {
    tabs.push({
      id: '2',
      label: nextMatchdayName?.trim() || teamPageTabs[1]?.label || 'Manage Team',
    });
  }

  return tabs;
};

export const teamPagePrices: DropdownOption[] = [
  { id: 'afford', label: 'Affordable', value: 'afford' },
  { id: '0-2', label: '£0-2m', value: '0-2' },
  { id: '2-4', label: '£2-4m', value: '2-4' },
  { id: '4-6', label: '£4-6m', value: '4-6' },
  { id: '6m+', label: '£6m+', value: '6-99' },
];

interface TeamPageTeamLabelFields {
  shirtKey?: string | null;
  clubName?: string | null;
  country?: string | null;
  contestantFlagKey?: string | null;
}

export const getTeamPagePlayerTeamLabel = (player: TeamPageTeamLabelFields): string | undefined => {
  return (
    player.clubName?.trim() ||
    player.country?.trim() ||
    player.contestantFlagKey?.trim() ||
    player.shirtKey?.trim() ||
    undefined
  );
};

/**
 * Builds the team filter options for the player picker from the players that are
 * actually in the pool. Full country names come from the backend teamName field
 * via player.clubName; missing legacy values fall back to the available code.
 */
export const buildTeamPageTeams = (players: TeamPageTeamLabelFields[]): DropdownOption[] => {
  const labelByValue = new Map<string, string>();
  for (const player of players) {
    const label = getTeamPagePlayerTeamLabel(player);
    if (!label) continue;

    const normalizedLabel = label.toLowerCase();
    if (labelByValue.has(normalizedLabel)) continue;

    labelByValue.set(normalizedLabel, label);
  }

  return [...labelByValue.values()]
    .sort((a, b) => a.localeCompare(b))
    .map(label => ({ id: label, label, value: label }));
};

export const teamPageFormationMap: Exclude<PlayerFiltersPosition, 'ALL'>[] = [
  'GK',
  'DEF',
  'MID',
  'STR',
];

export const TEAM_PAGE_DESKTOP_PLAYER_PANEL_PADDING_CLASSNAME = 'lg:pr-[571px]';
export const TEAM_PAGE_PITCH_BACKGROUND_CLASSNAME = 'flex-1 min-h-[528px] bg-[#2eb99b]';
export const TEAM_PAGE_PITCH_BACKGROUND_IMAGE_CLASSNAME =
  'h-[528px] translate-y-[12px] md:translate-y-[16px]';
export const TEAM_PAGE_PITCH_BACKGROUND_LARGE_IMAGE_CLASSNAME = 'md:hidden lg:block';
export const TEAM_PAGE_PITCH_BACKGROUND_SMALL_IMAGE_CLASSNAME = 'md:block lg:hidden';
export const TEAM_PAGE_PITCH_FORMATION_PADDING_CLASSNAME = 'lg:pr-[36vw]';
// TODO: Fetch initial budget from the current matchday API instead of hardcoding.
// This constant is used for UI display only; the backend now computes the
// authoritative budget from matchday.budget_initial when the team is created.
export const TEAM_PAGE_DEFAULT_BUDGET = 50_000_000;
export const TEAM_PAGE_NAVBAR_HEIGHT_PX = 52;
// Align desktop player panel with the in-page tabs/title row, not the global nav.
export const TEAM_PAGE_PLAYER_PANEL_TOP_OFFSET_PX = 160;
export const TEAM_PAGE_MOBILE_BREAKPOINT_QUERY = '(max-width: 1023px)';
export const TEAM_PAGE_TWELFTH_MAN_ICON_URL =
  'data:image/svg+xml,%3Csvg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"%3E%3Ccircle cx="10" cy="10" r="10" fill="%23EEC880"/%3E%3Ctext x="10" y="13.2" text-anchor="middle" font-family="Arial, sans-serif" font-size="9" font-weight="800" fill="%23222526"%3E12%3C/text%3E%3C/svg%3E';
export const TEAM_PAGE_MAX_CAPTAIN_ICON_URL =
  'data:image/svg+xml,%3Csvg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"%3E%3Ccircle cx="10" cy="10" r="10" fill="%23EEC880"/%3E%3Ctext x="10" y="13.2" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" font-weight="800" fill="%23222526"%3EC%3C/text%3E%3C/svg%3E';
export const TEAM_PAGE_GOAL_BONUS_ICON_URL =
  'data:image/svg+xml,%3Csvg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"%3E%3Ccircle cx="10" cy="10" r="10" fill="%23EEC880"/%3E%3Ccircle cx="10" cy="10" r="5.8" stroke="%23222526" stroke-width="1.4"/%3E%3Cpath d="M10 5.2L12.15 6.75L11.35 9.25H8.65L7.85 6.75L10 5.2Z" fill="%23222526"/%3E%3Cpath d="M8.65 9.25L6.65 10.75M11.35 9.25L13.35 10.75M10 5.2V3.9M6.65 10.75L5.35 13.2M13.35 10.75L14.65 13.2M8.3 14.8L6.65 10.75M11.7 14.8L13.35 10.75" stroke="%23222526" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/%3E%3C/svg%3E';
export const TEAM_PAGE_SUBSTITUTE_ICON_URL =
  'data:image/svg+xml,%3Csvg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"%3E%3Ccircle cx="10" cy="10" r="10" fill="%23EEC880"/%3E%3Cpath d="M10 4.2L14.5 5.85V9.45C14.5 12.45 12.62 14.56 10 15.8C7.38 14.56 5.5 12.45 5.5 9.45V5.85L10 4.2Z" stroke="%23222526" stroke-width="1.2" stroke-linejoin="round"/%3E%3Ctext x="10" y="11.7" text-anchor="middle" font-family="Arial, sans-serif" font-size="7" font-weight="800" fill="%23222526"%3ES%3C/text%3E%3C/svg%3E';

export const TEAM_PAGE_GAME_KEYS: GameKey[] = [
  // 'Assist',
  // 'Goal',
  // 'Own Goal',
  // 'Penalty Miss',
  // 'Penalty Save',
  // 'Red',
  // 'Sub Off',
  // 'Sub On',
  // 'Yellow',
  'Add Player',
  'Remove',
  'Injured',
  'Suspended',
  'Captain',
  'Vice Captain',
  'Unavailable',
  'Transfers',
  'Transfer Out',
  'Transfer In',
  'Reset',
];
